import { NavigationPosition, NavigationResolution } from '@unifyapps/defs/types/interface';
import SvgChevronRight from '@unifyapps/icons/outline/ChevronRight';
import SvgChevronRightSm from '@unifyapps/icons/outline/ChevronRightSm';
import { Box } from '@unifyapps/ui/components/Box';
import { Stack } from '@unifyapps/ui/components/Stack';
import { Tooltip } from '@unifyapps/ui/components/Tooltip';
import { Typography } from '@unifyapps/ui/components/Typography';
import { slowCn } from '@unifyapps/ui/lib/utils';
import { clsx } from 'clsx';
import { useEffect } from 'react';
import { useCollapse } from 'react-collapsed';
import { NavigationItemClassName } from '../constants';
import { isLabelVariantUpfront, isSelectionVariantSolid } from '../helpers';
import {
  groupNavItemLinkChevronVariants,
  navIconContainerVariants,
  navItemIconRootClassName,
  navItemLinkIconVariants,
  navItemLinkLabelVariants,
  navItemLinkRootVariants,
} from '../styles';
import type { NavigationItemSlotPropsType } from '../types';
import {
  ExpandableNavigationVariant,
  NavigationItemLabelVariantEnum,
  NavigationItemSelectionVariantEnum,
} from '../types';
import { NavigationIcon } from './NavigationIcon';

interface NavGroupProps {
  isActive?: boolean;
  isGroupOpen?: boolean;
  iconName?: string;
  activeIconName?: string;
  name: string;
  /**
   * Pass a function to read the group's open state — nested rows (e.g. LiteNestedSubItem) need it to
   * collapse themselves when this group closes.
   */
  children: React.ReactNode | ((open: boolean) => React.ReactNode);
  endNode?: React.ReactNode;
  /** Rendered at the group's end only while the group is collapsed (not expanded). */
  collapsedEndNode?: React.ReactNode;
  variant: 'lite' | 'rich';
  slotProps?: NavigationItemSlotPropsType;
}

const DEFAULT_SLOTPROPS = {
  navigationItem: {
    labelVariant: NavigationItemLabelVariantEnum.Expandable,
    selectionVariant: NavigationItemSelectionVariantEnum.Stroke,
  },
  container: {
    className: 'gap-y-xs',
  },
};

export default function NavigationGroup(props: NavGroupProps) {
  const {
    isActive = false,
    isGroupOpen = false,
    iconName,
    activeIconName,
    endNode,
    collapsedEndNode,
    name,
    children,
    variant,
    slotProps = DEFAULT_SLOTPROPS,
  } = props;

  const {
    expanded = true,
    labelVariant = NavigationItemLabelVariantEnum.Expandable,
    position = NavigationPosition.Start,
    resolution = NavigationResolution.XL,
    selectionVariant = NavigationItemSelectionVariantEnum.Stroke,
    expandableNavVariant = ExpandableNavigationVariant.SM,
    typographyClassName,
  } = slotProps.navigationItem as NavigationItemSlotPropsType['navigationItem'];

  // declared before the variants below because `open` decides whether this row still shows the fill.
  const {
    getCollapseProps,
    getToggleProps,
    isExpanded: open,
    setExpanded,
  } = useCollapse({
    defaultExpanded: isGroupOpen,
    easing: 'ease-in-out',
    duration: 150,
  });

  const navItemIconRootClassname = navItemIconRootClassName({ hasIcon: Boolean(iconName) });

  // On the lite rail selection is a background on the deepest *visible* row, so an open group hands
  // it to the selected descendant now showing it — highlighting both would name one location twice.
  // The rich rail marks an active group whatever its state.
  const isFillActive = variant === 'lite' ? isActive && !open : isActive;

  const rootClassname = navItemLinkRootVariants({
    className: clsx('cursor-pointer', navItemIconRootClassname),
    active: isFillActive,
    variant,
    labelVariant,
    selectionVariant,
    position,
    resolution,
    expandableNavVariant,
    expanded,
  });

  const iconClassname = navItemLinkIconVariants({
    className: 'shrink-0',
    active: isActive,
    variant,
  });

  const navIconContainerClassName = navIconContainerVariants({
    labelVariant,
    selectionVariant,
    position,
    active: isActive,
    variant,
    resolution,
  });

  const labelClassname = navItemLinkLabelVariants({
    active: isActive,
    expanded,
    variant,
    labelVariant,
    selectionVariant,
    position,
  });

  const chevronClassName = groupNavItemLinkChevronVariants({
    active: isActive,
    open,
    expanded,
    variant,
    labelVariant,
    selectionVariant,
    position,
  });

  useEffect(() => {
    if (!expanded) {
      setExpanded(false);
    }
  }, [expanded, setExpanded]);

  // When the group is collapsed and carries a collapsed-only end node (e.g. a total
  // count pill), it occupies the chevron's slot; the chevron is swapped in on row hover.
  const showCollapsedEndNode = !open && Boolean(collapsedEndNode);

  // lite gets the 16px chevron, rich the 24px one; both point right and are rotated by the chevron
  // variant when open.
  const ChevronIcon = variant === 'lite' ? SvgChevronRightSm : SvgChevronRight;

  const el = (
    <Stack className={clsx(NavigationItemClassName[position], slotProps.container?.className)}>
      <Box
        // To prevent Hydration mismatch since we have Iconbutton rendered inside this
        // box in case of solid Variant, or else we will have nested buttons
        as={isSelectionVariantSolid(selectionVariant) ? 'div' : 'button'}
        className={clsx('group/navitem', rootClassname, {
          'flex flex-col': isLabelVariantUpfront(labelVariant),
        })}
        {...getToggleProps()}
      >
        {iconName ? (
          <Box className={navIconContainerClassName}>
            <Box
              className={clsx({
                hidden: activeIconName && isActive,
              })}
            >
              <NavigationIcon
                iconName={iconName}
                isActive={isActive}
                label={name}
                selectionVariant={selectionVariant}
                // Since in case of solid variant, we want to apply styles on icon
                // itself and not iconbutton
                {...(selectionVariant === NavigationItemSelectionVariantEnum.Stroke
                  ? { className: iconClassname }
                  : { iconClassname })}
              />
            </Box>
            {activeIconName ? (
              <Box
                className={clsx({
                  hidden: !isActive,
                })}
              >
                <NavigationIcon
                  iconName={activeIconName}
                  isActive={isActive}
                  label={name}
                  selectionVariant={selectionVariant}
                  // Since in case of solid variant, we want to apply styles on icon
                  // itself and not iconbutton
                  {...(selectionVariant === NavigationItemSelectionVariantEnum.Stroke
                    ? { className: iconClassname }
                    : { iconClassname })}
                />
              </Box>
            ) : null}
          </Box>
        ) : null}
        {expanded || labelVariant === NavigationItemLabelVariantEnum.Upfront ? (
          <Typography className={slowCn(labelClassname, typographyClassName)}>{name}</Typography>
        ) : null}
        <Box className="ms-auto flex items-center gap-md">
          {endNode}
          {showCollapsedEndNode ? (
            <>
              <Box className="flex group-hover/navitem:hidden">{collapsedEndNode}</Box>
              <Box className="hidden group-hover/navitem:flex">
                <ChevronIcon className={chevronClassName} />
              </Box>
            </>
          ) : (
            <ChevronIcon className={chevronClassName} />
          )}
        </Box>
      </Box>
      {/* lite only, so the rich rail keeps the markup it has always shipped. Here the collapsing
          element is a plain block and the flex column sits on its child — the shape the platform
          rail's group uses. react-collapsed animates to a scrollHeight measured off this element,
          and a flex container reports that differently from a block (bottom margin of the last
          child), so the two roles are kept apart. The icon-group marker stays on the outer element
          either way — descendants size their glyphs off it. */}
      {variant === 'lite' ? (
        <Box className={navItemIconRootClassname} {...getCollapseProps()}>
          <Stack>{typeof children === 'function' ? children(open) : children}</Stack>
        </Box>
      ) : (
        <Stack
          className={clsx('overflow-hidden', navItemIconRootClassname)}
          {...getCollapseProps()}
        >
          {typeof children === 'function' ? children(open) : children}
        </Stack>
      )}
    </Stack>
  );

  return labelVariant === NavigationItemLabelVariantEnum.Tooltip ? (
    <Tooltip placement="right" title={name}>
      {el}
    </Tooltip>
  ) : (
    el
  );
}
